
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";

export type DuplicateGroup = {
  key: string;
  contractors: Contractor[];
};

/**
 * Fetches all contractors from Supabase and identifies potential duplicates
 * based on name, service_type, and location combinations.
 */
export const checkForDuplicateContractors = async (): Promise<DuplicateGroup[]> => {
  try {
    console.log("Checking for duplicate contractors...");
    
    // Fetch all contractors from the database
    const { data: contractors, error } = await supabase
      .from('contractors')
      .select('*');
    
    if (error) {
      console.error("Error fetching contractors:", error);
      throw error;
    }
    
    if (!contractors || contractors.length === 0) {
      console.log("No contractors found in the database");
      return [];
    }
    
    console.log(`Found ${contractors.length} total contractors`);
    
    // Group contractors by name (case-insensitive)
    const nameGroups: Record<string, Contractor[]> = {};
    contractors.forEach(contractor => {
      const normalizedName = contractor.name.toLowerCase().trim();
      if (!nameGroups[normalizedName]) {
        nameGroups[normalizedName] = [];
      }
      nameGroups[normalizedName].push(contractor as Contractor);
    });
    
    // Filter groups to only include those with more than one contractor
    const duplicateGroups: DuplicateGroup[] = [];
    
    // Check for name duplicates
    Object.entries(nameGroups)
      .filter(([_, group]) => group.length > 1)
      .forEach(([name, group]) => {
        duplicateGroups.push({
          key: `Name: ${name}`,
          contractors: group
        });
      });
    
    // Also check for duplicates by name + service_type + location combination
    const combinationGroups: Record<string, Contractor[]> = {};
    contractors.forEach(contractor => {
      const key = `${contractor.name.toLowerCase().trim()}_${contractor.service_type.toLowerCase().trim()}_${contractor.location.toLowerCase().trim()}`;
      if (!combinationGroups[key]) {
        combinationGroups[key] = [];
      }
      combinationGroups[key].push(contractor as Contractor);
    });
    
    // Add combination duplicates
    Object.entries(combinationGroups)
      .filter(([_, group]) => group.length > 1)
      .forEach(([key, group]) => {
        const [name, service, location] = key.split('_');
        duplicateGroups.push({
          key: `Name: ${name}, Service: ${service}, Location: ${location}`,
          contractors: group
        });
      });
    
    console.log(`Found ${duplicateGroups.length} groups of potential duplicates`);
    return duplicateGroups;
  } catch (error) {
    console.error("Error checking for duplicates:", error);
    throw error;
  }
};
